import { memo, type ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { getAnnouncementAnchorTargetProps } from './useAnnouncementActionClick';

export type AnnouncementBodyTextProps = {
  body: string;
  /** Single-line surfaces (next to a title). Block markdown is unwrapped so nothing block-level is emitted. */
  inline?: boolean;
};

const HTTP_URL_PREFIX = 'http://';

type AnnouncementHeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

// Action urls only treat https as external. Markdown bodies also open plain http in a new tab.
const getAnnouncementMarkdownLinkTargetProps = (href: string) => {
  if (href.startsWith(HTTP_URL_PREFIX)) {
    return { rel: 'noopener noreferrer', target: '_blank' };
  }

  return getAnnouncementAnchorTargetProps(href);
};

const renderAnnouncementHeading = (tag: AnnouncementHeadingTag, children: ReactNode) => {
  const Tag = tag;

  return <Tag className="mb-1 font-semibold last:mb-0">{children}</Tag>;
};

/**
 * Must stay a module constant. react-markdown treats each entry as a component type, so a new map
 * per render remounts the markdown subtree.
 */
const ANNOUNCEMENT_BODY_MARKDOWN_COMPONENTS: Components = {
  a: ({ children, href }) => (
    <a
      className="underline underline-offset-2"
      href={href}
      {...(href ? getAnnouncementMarkdownLinkTargetProps(href) : {})}
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-1 border-current border-l-2 pl-2 last:mb-0">{children}</blockquote>
  ),
  code: ({ children }) => (
    <code className="rounded-4 ads:bg-ads-gray-4 bg-gray-4 px-1 font-mono text-10">{children}</code>
  ),
  del: ({ children }) => <del className="line-through">{children}</del>,
  h1: ({ children }) => renderAnnouncementHeading('h1', children),
  h2: ({ children }) => renderAnnouncementHeading('h2', children),
  h3: ({ children }) => renderAnnouncementHeading('h3', children),
  h4: ({ children }) => renderAnnouncementHeading('h4', children),
  h5: ({ children }) => renderAnnouncementHeading('h5', children),
  h6: ({ children }) => renderAnnouncementHeading('h6', children),
  hr: () => <hr className="my-1 border-current" />,
  // Authors can paste any URL; do not hotlink or let a natural-size image overflow the card.
  img: () => null,
  ol: ({ children }) => <ol className="mb-1 list-decimal space-y-0.5 pl-4 last:mb-0">{children}</ol>,
  p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
  pre: ({ children }) => (
    <pre className="mb-1 overflow-x-auto font-mono text-10 last:mb-0 [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  table: ({ children }) => (
    <div className="mb-1 max-w-full overflow-x-auto last:mb-0">
      <table className="w-full text-left">{children}</table>
    </div>
  ),
  td: ({ children }) => <td className="py-0.5 pr-2 align-top">{children}</td>,
  th: ({ children }) => <th className="border-current border-b py-0.5 pr-2 text-left font-semibold">{children}</th>,
  ul: ({ children }) => <ul className="mb-1 list-disc space-y-0.5 pl-4 last:mb-0">{children}</ul>,
};

const ANNOUNCEMENT_INLINE_ALLOWED_ELEMENTS = ['strong', 'em', 'a', 'code', 'del', 'br'];

const ANNOUNCEMENT_BODY_REMARK_PLUGINS = [remarkGfm, remarkBreaks];

export const AnnouncementBodyText = memo(({ body, inline = false }: AnnouncementBodyTextProps) => {
  if (inline) {
    return (
      <Markdown
        allowedElements={ANNOUNCEMENT_INLINE_ALLOWED_ELEMENTS}
        components={ANNOUNCEMENT_BODY_MARKDOWN_COMPONENTS}
        remarkPlugins={ANNOUNCEMENT_BODY_REMARK_PLUGINS}
        unwrapDisallowed
      >
        {body}
      </Markdown>
    );
  }

  return (
    <Markdown components={ANNOUNCEMENT_BODY_MARKDOWN_COMPONENTS} remarkPlugins={ANNOUNCEMENT_BODY_REMARK_PLUGINS}>
      {body}
    </Markdown>
  );
});

AnnouncementBodyText.displayName = 'AnnouncementBodyText';
